import type { UseMutationReturnType, UseQueryReturnType } from '@tanstack/vue-query'
import type { PagedResults } from '@/types/paging/paged-results.model'
import type { PagingSortingFilteringDetails } from '@/types/paging/paging-sorting-filtering-details.model'
import type { MutationOptions, QueryOptions } from '@/types/query-options.model'
import type { SiteForUser } from '@/types/site.model'
import type { User } from '@/types/user.model'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/vue-query'
import { USER_KEYS } from '@/utils/factories/keys'
import { deleteRequest, getRequest, postPagedRequest, postRequest, putRequest } from './core/api-client'

export async function getCurrentUser(signal?: AbortSignal | undefined): Promise<User> {
  return await getRequest<User>('/users/current', {
    signal,
  })
}

export function getCurrentUserQuery(useQueryOptions?: QueryOptions<User>): UseQueryReturnType<User, Error> {
  return useQuery({
    queryKey: USER_KEYS.current(),
    queryFn: async ({ signal }) => await getCurrentUser(signal),
    ...useQueryOptions,
  })
}

// GET /users/:id
async function getUser(id: string, signal?: AbortSignal | undefined): Promise<User> {
  return await getRequest<User>(`/users/${id}`, { signal })
}

export function getUserQuery(id: string, useQueryOptions?: QueryOptions<User>): UseQueryReturnType<User, Error> {
  return useQuery({
    queryKey: USER_KEYS.byId(id),
    queryFn: async ({ signal }) => await getUser(id, signal),
    ...useQueryOptions,
  })
}

// GET /users/current/sites
async function getUserSites(signal?: AbortSignal | undefined): Promise<SiteForUser[]> {
  return await getRequest<SiteForUser[]>(`/users/current/sites`, { signal })
}

export function getUserSitesQuery(useQueryOptions?: QueryOptions<SiteForUser[]>): UseQueryReturnType<SiteForUser[], Error> {
  return useQuery({
    queryKey: USER_KEYS.sites(),
    queryFn: async ({ signal }) => await getUserSites(signal),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...useQueryOptions,
  })
}

// POST /users
async function createUser(user: User): Promise<User> {
  return await postRequest<User, User>(`/users`, user)
}

export function createUserMutation(useMutationOptions?: MutationOptions<User>): UseMutationReturnType<User, Error, User, Error> {
  return useMutation({
    mutationFn: async (user: User) => await createUser(user),
    ...useMutationOptions,
  })
}

// PUT /users/:id
async function updateUser(User: User): Promise<User> {
  return await putRequest<User, User>(`/users/${User.id}`, User)
}

export function updateUserMutation(useMutationOptions?: MutationOptions<User>): UseMutationReturnType<User, Error, User, Error> {
  return useMutation({
    mutationFn: async (user: User) => await updateUser(user),
    ...useMutationOptions,
  })
}

// DELETE /users/:id
async function archiveUser(id: string): Promise<boolean> {
  return await deleteRequest(`/users/${id}`)
}

export function archiveUserMutation(useMutationOptions?: MutationOptions<boolean>): UseMutationReturnType<boolean, Error, string, Error> {
  return useMutation({
    mutationFn: async (id: string) => await archiveUser(id),
    ...useMutationOptions,
  })
}

// POST users/organisation
async function getOrganisationUsers(pagingSortingFilteringDetails: PagingSortingFilteringDetails, signal?: AbortSignal | undefined): Promise<PagedResults<User>> {
  return await postPagedRequest<User>(`users/organisation`, pagingSortingFilteringDetails, { signal })
}

export function getOrganisationUsersQuery(pagingSortingFilteringDetails: PagingSortingFilteringDetails): UseQueryReturnType<PagedResults<User>, Error> {
  return useQuery({
    queryKey: USER_KEYS.orgWithParams(pagingSortingFilteringDetails),
    queryFn: async ({ signal }) => await getOrganisationUsers(pagingSortingFilteringDetails, signal),
    placeholderData: keepPreviousData,
  })
}

// POST users/partner
async function getPartnerUsers(pagingSortingFilteringDetails: PagingSortingFilteringDetails, signal?: AbortSignal | undefined): Promise<PagedResults<User>> {
  return await postPagedRequest<User>(`users/partner`, pagingSortingFilteringDetails, { signal })
}

export function getPartnerUsersQuery(pagingSortingFilteringDetails: PagingSortingFilteringDetails): UseQueryReturnType<PagedResults<User>, Error> {
  return useQuery({
    queryKey: USER_KEYS.partnerWithParams(pagingSortingFilteringDetails),
    queryFn: async ({ signal }) => await getPartnerUsers(pagingSortingFilteringDetails, signal),
    placeholderData: keepPreviousData,
  })
}

// POST users/sys-admins
async function getSysAdminUsers(pagingSortingFilteringDetails: PagingSortingFilteringDetails, signal?: AbortSignal | undefined): Promise<PagedResults<User>> {
  return await postPagedRequest<User>(`users/sys-admins`, pagingSortingFilteringDetails, { signal })
}

export function getSysAdminUsersQuery(pagingSortingFilteringDetails: PagingSortingFilteringDetails): UseQueryReturnType<PagedResults<User>, Error> {
  return useQuery({
    queryKey: USER_KEYS.sysAdminWithParams(pagingSortingFilteringDetails),
    queryFn: async ({ signal }) => await getSysAdminUsers(pagingSortingFilteringDetails, signal),
    placeholderData: keepPreviousData,
  })
}
